import { useTranslation } from 'react-i18next';
import { BookMarked, Brain, RefreshCw, Shield, Search, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';
import SEOHead from '@/components/SEOHead';
import { BrandIcons } from '@/components/ui/brand-icons';

const TwitterIcon = BrandIcons.twitter;

export default function XBookmarksLanding() {
  const { t } = useTranslation('integrations');

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleOpenDocs = () => {
    window.location.href = '/docs/integrations/x-bookmarks';
  };

  const setupSteps = t('xBookmarksLanding.setupSteps', {
    returnObjects: true,
  }) as string[];

  return (
    <>
      <SEOHead
        title={t('xBookmarksLanding.seoTitle')}
        description={t('xBookmarksLanding.seoDescription')}
        keywords={[
          'X bookmarks AI memory',
          'import X bookmarks to Memara',
          'Twitter bookmarks semantic search',
          'X bookmark sync',
          'persistent memory X',
          'Memara X integration',
        ]}
        ogType="article"
        priority="high"
      />
      <div className="sr-only">{t('xBookmarksLanding.seoKeywordsHidden')}</div>
      <IntegrationLandingBase
        registryIntegrationId="twitter_bookmarks"
        icon={
          <span
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-50 shadow"
            aria-label={t('xBookmarksLanding.iconAria')}
          >
            <TwitterIcon className="w-10 h-10" />
          </span>
        }
        title={t('xBookmarksLanding.title')}
        description={t('xBookmarksLanding.description')}
        microcopy={t('xBookmarksLanding.microcopy')}
        trustedBy={t('xBookmarksLanding.trustedBy')}
        features={[
          {
            icon: <BookMarked className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.import.title'),
            description: t('xBookmarksLanding.features.import.description'),
          },
          {
            icon: <Search className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.search.title'),
            description: t('xBookmarksLanding.features.search.description'),
          },
          {
            icon: <RefreshCw className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.sync.title'),
            description: t('xBookmarksLanding.features.sync.description'),
          },
          {
            icon: <Shield className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.oauth.title'),
            description: t('xBookmarksLanding.features.oauth.description'),
          },
          {
            icon: <Database className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.spaces.title'),
            description: t('xBookmarksLanding.features.spaces.description'),
          },
          {
            icon: <Brain className="h-4 w-4 text-sky-600" />,
            title: t('xBookmarksLanding.features.agents.title'),
            description: t('xBookmarksLanding.features.agents.description'),
          },
        ]}
        setupSteps={Array.isArray(setupSteps) ? setupSteps : []}
        ctas={[
          {
            label: t('xBookmarksLanding.ctaGetStarted'),
            onClick: handleGetStarted,
            className:
              'bg-sky-600 hover:bg-sky-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
          },
          {
            label: t('xBookmarksLanding.ctaDocs'),
            onClick: handleOpenDocs,
            className:
              'border-sky-200 text-sky-600 hover:bg-sky-50 px-8 py-4 h-11 rounded-md text-base border',
          },
        ]}
        screenshots={[
          {
            id: 'x-bookmarks-connect',
            title: t('xBookmarksLanding.screenshots.connect.title'),
            description: t('xBookmarksLanding.screenshots.connect.description'),
            placeholder: t('xBookmarksLanding.screenshots.connect.placeholder'),
            badge: t('xBookmarksLanding.screenshots.connect.badge'),
          },
          {
            id: 'x-bookmarks-backfill',
            title: t('xBookmarksLanding.screenshots.backfill.title'),
            description: t('xBookmarksLanding.screenshots.backfill.description'),
            placeholder: t('xBookmarksLanding.screenshots.backfill.placeholder'),
            badge: t('xBookmarksLanding.screenshots.backfill.badge'),
          },
          {
            id: 'x-bookmarks-search',
            title: t('xBookmarksLanding.screenshots.search.title'),
            description: t('xBookmarksLanding.screenshots.search.description'),
            placeholder: t('xBookmarksLanding.screenshots.search.placeholder'),
            badge: t('xBookmarksLanding.screenshots.search.badge'),
          },
        ]}
      >
        <p className="text-center text-sm text-gray-500 pb-8">
          {t('xBookmarksLanding.footerNote')}{' '}
          <Link to="/blog/x-bookmarks-integration-launch" className="text-sky-600 underline">
            {t('xBookmarksLanding.footerBlogLink')}
          </Link>
        </p>
      </IntegrationLandingBase>
    </>
  );
}
